import { ref, watch } from 'vue';
import { showToast } from 'vant';
import { fetchShopList, fetchShopKitchenList } from '@/service/base';
import type { ShopDetailModel } from '@/model/shop';
import { SELECTED_SHOP_KEY } from '@/utils/selectedShop';

type CacheScope = 'local' | 'session';
const readCache = (key: string, scope: CacheScope): string => {
  try {
    return (scope === 'session' ? sessionStorage : localStorage).getItem(key) || '';
  } catch (error) {
    console.warn('读取餐厅厨房缓存失败', error);
    return '';
  }
};
const writeCache = (key: string, value: string, scope: CacheScope): void => {
  try {
    (scope === 'session' ? sessionStorage : localStorage).setItem(key, value);
  } catch (error) {
    console.warn('保存餐厅厨房缓存失败', error);
  }
};

export const useRestaurantKitchen = (kitchenKey = '', cacheScope: CacheScope = 'local') => {
  const shops = ref<ShopDetailModel[]>([]);
  const currentShop = ref('');
  const kitchens = ref<{ id: string; name: string }[]>([]);
  const currentKitchen = ref('');
  const selectionVersion = ref(0);
  const selectionLoading = ref(false);
  const selectionMessage = ref('');
  watch(
    currentKitchen,
    (value: string) => {
      selectionVersion.value++;
      if (value && kitchenKey && currentShop.value) {
        writeCache(`${kitchenKey}_${currentShop.value}`, value, cacheScope);
      }
    },
    { flush: 'sync' },
  );
  let kitchenRequest = 0;
  const loadKitchens = async (): Promise<void> => {
    const request = ++kitchenRequest;
    selectionVersion.value++;
    currentKitchen.value = '';
    kitchens.value = [];
    const requestVersion = selectionVersion.value;
    if (!currentShop.value) {
      selectionLoading.value = false;
      return;
    }
    // 餐厅与全局菜单共用缓存；cacheScope 仅隔离厨房选择，避免刷新后恢复旧餐厅。
    writeCache(SELECTED_SHOP_KEY, currentShop.value, 'local');
    selectionLoading.value = true;
    selectionMessage.value = '';
    try {
      const response = await fetchShopKitchenList({
        pageNum: 1,
        pageSize: 999,
        data: { shopId: Number(currentShop.value), status: 0 },
      });
      if (requestVersion !== selectionVersion.value) return;
      if (response.code !== 200) throw new Error(response.msg || '加载厨房失败');
      kitchens.value = [
        ...new Map(
          (response.result?.list || []).map((item) => [
            String(item.kitchenId),
            { id: String(item.kitchenId), name: item.kitchenName },
          ]),
        ).values(),
      ];
      if (!kitchens.value.length) {
        selectionMessage.value = '当前餐厅暂无可用厨房';
        return;
      }
      const saved = kitchenKey
        ? readCache(`${kitchenKey}_${currentShop.value}`, cacheScope) ||
          readCache(kitchenKey, cacheScope)
        : '';
      currentKitchen.value =
        saved === 'all' || kitchens.value.some((item) => item.id === saved) ? saved : 'all';
    } catch (error) {
      if (requestVersion !== selectionVersion.value) return;
      console.error('加载餐厅厨房失败', error);
      selectionMessage.value = '加载厨房失败，请刷新重试';
      showToast(selectionMessage.value);
    } finally {
      if (request === kitchenRequest) selectionLoading.value = false;
    }
  };
  watch(
    currentShop,
    () => {
      void loadKitchens();
    },
    { flush: 'sync' },
  );
  const loadShops = async (): Promise<void> => {
    try {
      const response = await fetchShopList({ pageNum: 1, pageSize: 999, data: { shopStatus: 0 } });
      if (response.code !== 200) throw new Error(response.msg || '加载餐厅失败');
      shops.value = response.result?.list || [];
      const saved = readCache(SELECTED_SHOP_KEY, 'local');
      currentShop.value = shops.value.some((item) => String(item.shopId) === saved)
        ? saved
        : String(shops.value[0]?.shopId || '');
      if (!currentShop.value) selectionMessage.value = '暂无可用餐厅';
    } catch (error) {
      console.error('加载餐厅失败', error);
      selectionMessage.value = '加载餐厅失败，请刷新重试';
      showToast(selectionMessage.value);
    }
  };
  return {
    shops,
    currentShop,
    kitchens,
    currentKitchen,
    selectionVersion,
    selectionLoading,
    selectionMessage,
    loadShops,
  };
};
